import { useEffect, useState } from "react";
import { useLang } from "../i18n";
import api from "../lib/api";
import { fmtSigned, pnlColor } from "../lib/format";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";

const TT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#05050A] border border-[#1E2028] px-3 py-2 text-xs font-mono">
      <div className="text-[#6B7280]">{label}</div>
      <div className="text-white">{fmtSigned(payload[0].value)}</div>
    </div>
  );
};

function Stat({ label, value, valueClass = "" }) {
  return (
    <div className="border border-[#1E2028] bg-[#05050A] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono">{label}</div>
      <div className={`mt-1 text-lg font-mono tabular ${valueClass}`}>{value}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="border border-[#1E2028] bg-[#0F1015] p-4">
      <div className="text-xs uppercase tracking-wider text-[#9CA3AF] font-mono mb-3">{title}</div>
      {children}
    </div>
  );
}

export default function PerformancePanel({ refreshKey }) {
  const { t } = useLang();
  const [perf, setPerf] = useState(null);

  useEffect(() => { api.get("/performance").then((r) => setPerf(r.data)).catch(() => {}); }, [refreshKey]);
  if (!perf) return null;
  const s = perf.stats;
  const empty = perf.capital_curve.length === 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat label={t("trades")} value={s.trades} />
        <Stat label={t("winRate")} value={`${s.win_rate}%`} valueClass="text-[#FACC15]" />
        <Stat label={t("profitFactor")} value={s.profit_factor} valueClass={s.profit_factor >= 1 ? "text-[#10B981]" : "text-[#EF4444]"} />
        <Stat label={t("avgWin")} value={fmtSigned(s.avg_win)} valueClass="text-[#10B981]" />
        <Stat label={t("avgLoss")} value={fmtSigned(s.avg_loss)} valueClass="text-[#EF4444]" />
        <Stat label={t("totalPnl")} value={fmtSigned(s.total_pnl)} valueClass={pnlColor(s.total_pnl)} />
      </div>

      {empty ? (
        <div className="border border-[#1E2028] bg-[#0F1015] py-16 text-center text-sm text-[#6B7280] font-mono">
          {t("noHistory")}
        </div>
      ) : (
        <>
          <Panel title={t("capitalCurve")}>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={perf.capital_curve}>
                <CartesianGrid stroke="#1E2028" vertical={false} />
                <XAxis dataKey="t" stroke="#6B7280" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <YAxis stroke="#6B7280" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} domain={["auto", "auto"]} />
                <Tooltip content={<TT />} />
                <Line type="monotone" dataKey="equity" stroke="#FACC15" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title={t("dailyPnl")}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={perf.daily_pnl}>
                  <CartesianGrid stroke="#1E2028" vertical={false} />
                  <XAxis dataKey="day" stroke="#6B7280" tick={{ fontSize: 9, fontFamily: "IBM Plex Mono" }} />
                  <YAxis stroke="#6B7280" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                  <Tooltip content={<TT />} cursor={{ fill: "#17181F" }} />
                  <Bar dataKey="pnl">
                    {perf.daily_pnl.map((d, i) => <Cell key={i} fill={d.pnl >= 0 ? "#10B981" : "#EF4444"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title={t("byStrategy")}>
              <div className="space-y-2 pt-1">
                {perf.by_strategy.map((r) => (
                  <div key={r.name} className="flex items-center justify-between text-sm">
                    <span className="text-[#D1D5DB]">{r.name}</span>
                    <span className={`font-mono ${pnlColor(r.pnl)}`}>{fmtSigned(r.pnl)}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          <Panel title={t("byInstrument")}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {perf.by_instrument.map((r) => (
                <div key={r.name} className="border border-[#1E2028] bg-[#05050A] p-2">
                  <div className="text-xs font-display font-semibold">{r.name}</div>
                  <div className={`text-sm font-mono ${pnlColor(r.pnl)}`}>{fmtSigned(r.pnl)}</div>
                </div>
              ))}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
